import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Helmet } from 'react-helmet-async'

import { TextHeadline2, TextBody, Link } from 'blocket-ui'
import { sendEvent } from 'tracking/gtm'

import SkipLink from 'components/SkipLink'
import { FullHeightWrapper } from 'components/LayoutStyles'
import Footer from 'components/Footer'
import TabBar from 'components/TabBar'
import Illustration from 'components/Illustration'
import Status from 'components/Status'

const ImageContainer = styled.div`
  margin-bottom: 8px;
  @media (max-height: 500px) {
    .errorImage {
      display: none !important;
    }
  }
`
const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: white;
  flex: 1 1 auto;
  margin: 16px;
`

export const ErrorImage = ({ image, maxWidth = 280, baseUrl }) => (
  <ImageContainer>
    <Illustration image={image} maxWidth={maxWidth} baseUrl={baseUrl} />
  </ImageContainer>
)

ErrorImage.propTypes = {
  image: PropTypes.string.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  baseUrl: PropTypes.string,
}

const getComponent = (baseUrl, image, heading, bodyText, linkText, linkUrl = '/') => (
  <Container id="skip-tabbar">
    <ErrorImage image={image} baseUrl={baseUrl} />
    <TextHeadline2>{heading}</TextHeadline2>
    <TextBody style={{ margin: '16px', maxWidth: '485px' }}>
      {bodyText.map((text) => (
        <p key={text} style={{ textAlign: 'center' }}>
          {text}
        </p>
      ))}
    </TextBody>
    {linkText && (
      <Link to={linkUrl} external>
        {linkText}
      </Link>
    )}
  </Container>
)

const ErrorComponent = ({ error, baseUrl, noIndex }) => {
  let child
  let imageName
  let header
  let bodyText
  let linkText
  let linkUrl
  let statusCode

  useEffect(() => {
    sendEvent({
      event: 'view',
      eventType: 'View',
      pageType: 'errorpages',
      objectType: 'Error',
      domain: 'errorpages',
      subDomain: '404',
      objectId: `error_${statusCode}`,
      objectName: `error ${statusCode}`,
      objectPath: `/404.html?path=${escape(encodeURI(window.location.href))}`,
    })
  }, [error, statusCode])

  const noIndexPages = [
    'no_adwatch_ads',
    'store_not_found',
    '404',
    'ad_not_found',
    'error_no_such_adwatch',
  ]

  switch (error) {
    case 'no_adwatch_ads':
    case 'error_no_such_adwatch':
      imageName = 'hittade-inga-annonser'
      header = 'Här var det tomt!'
      bodyText = ['Inga annonser hittades för denna bevakning']
      statusCode = 500
      return getComponent(baseUrl, imageName, header, bodyText, linkText)

    case 'validation_error':
    case 'no_ads_found':
      imageName = 'oops'
      header = 'Här var det tomt!'
      bodyText = ['Annonsen du letar efter finns inte på Blocket.']
      linkText = 'Kika bland våra andra annonser istället!'
      linkUrl = '/annonser'
      statusCode = 404
      child = getComponent(baseUrl, imageName, header, bodyText, linkText, linkUrl)
      break

    case 'failed_to_fetch':
      imageName = 'hittade-inga-annonser'
      header = 'Ånej!'
      bodyText = ['Tyvärr verkar det inte funka just nu. Försök igen lite senare.']
      statusCode = 500
      child = getComponent(baseUrl, imageName, header, bodyText, linkText)
      break

    case 'store_not_found':
      imageName = 'oops'
      header = 'Hoppsan'
      bodyText = ['Butiken kunde inte hittas, prova gärna igen lite senare.']
      linkText = 'Till startsidan'
      statusCode = 404
      child = getComponent(baseUrl, imageName, header, bodyText, linkText)
      break

    case '404':
      imageName = 'oops'
      header = 'Hoppsan, 404 Error'
      bodyText = ['Vi kunde inte hitta sidan som du letade efter.']
      linkText = 'Hitta tillbaka till Blocket'
      statusCode = 404
      child = getComponent(baseUrl, imageName, header, bodyText, linkText)
      break

    case '500':
      imageName = 'needhelp'
      header = 'Ånej!'
      bodyText = ['Något blev fel. Försök igen.']
      linkText = 'Tillbaka till startsidan'
      statusCode = 500
      child = getComponent(baseUrl, imageName, header, bodyText, linkText)
      break

    case 'ad_not_found':
      imageName = 'oops'
      header = 'Hittade inte annonsen…'
      bodyText = [
        'Den här varan finns inte på Blocket. Förmodligen beror det på att någon hann före dig eller så har annonsören tagit bort sin annons.',
        'Om du just lagt in eller ändrat den här annonsen, kom ihåg att det kan dröja en halvtimme innan den visas.',
      ]
      linkText = 'Till startsidan'
      statusCode = 404
      child = getComponent(baseUrl, imageName, header, bodyText, linkText)
      break

    default:
      imageName = 'oops'
      header = 'Ånej!'
      bodyText = ['Något blev fel. Försök igen.']
      linkText = 'Tillbaka till startsidan'
      linkUrl = '/'
      statusCode = 500
      child = getComponent(baseUrl, imageName, header, bodyText, linkText)
      break
  }
  const shouldNotBeIndexed = noIndex || noIndexPages.indexOf(error) !== -1

  return (
    <FullHeightWrapper>
      {shouldNotBeIndexed && (
        <Helmet>
          <meta key="robots" name="robots" content="noindex, nofollow" />
          <meta key="googlebot" name="googlebot" content="none" />
        </Helmet>
      )}
      <SkipLink to="#skip-tabbar" />
      <TabBar />
      <Status statusCode={statusCode}>{child}</Status>
      <Footer />
    </FullHeightWrapper>
  )
}

ErrorComponent.propTypes = {
  error: PropTypes.string,
  baseUrl: PropTypes.string,
  noIndex: PropTypes.bool,
}

export default ErrorComponent
